import type { ReactNode } from "react";
import type { WheelSegmentV1 } from "../../types/wheel";
import { polarToXY } from "./wheelGeometry";

const PALETTE = ["#e8ecf4", "#f1f4f9", "#dde3ee", "#eef2f8"];

interface SimpleWheelProps {
  /** Must be the spinable list (same order as API `resultIndex`) */
  segments: WheelSegmentV1[];
  rotationDeg: number;
  size?: number;
  className?: string;
}

/**
 * SVG wheel: weighted arcs, labels, center hub, top pointer (fixed).
 */
export function SimpleWheel({ segments, rotationDeg, size = 280, className }: SimpleWheelProps) {
  const vb = 200;
  const cx = 0;
  const cy = 0;
  const outerR = vb - 24;
  const innerR = 36;
  const total = segments.reduce((s, x) => s + x.weight, 0);

  if (segments.length === 0 || total <= 0) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: "1px dashed #cbd5e1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "1rem",
          color: "#64748b",
          fontSize: 13,
        }}
      >
        Add at least two enabled segments with positive weights to show the wheel.
      </div>
    );
  }

  let startDeg = 0;
  const arcs: ReactNode[] = [];
  const labels: ReactNode[] = [];

  segments.forEach((seg, i) => {
    const sweep = total > 0 ? (seg.weight / total) * 360 : 0;
    const endDeg = startDeg + sweep;
    const mid = startDeg + sweep / 2;
    const color = PALETTE[i % PALETTE.length];

    const [x1, y1] = polarToXY(cx, cy, outerR, startDeg);
    const [x2, y2] = polarToXY(cx, cy, outerR, endDeg);
    const largeArc = sweep > 180 ? 1 : 0;

    const d = [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2} ${y2}`,
      "Z",
    ].join(" ");

    arcs.push(<path key={`arc-${seg.id}`} d={d} fill={color} stroke="#cbd5e1" strokeWidth={1} />);

    const [lx, ly] = polarToXY(cx, cy, outerR * 0.62, mid);
    const label = seg.label.length > 18 ? `${seg.label.slice(0, 16)}…` : seg.label;
    labels.push(
      <text
        key={`txt-${seg.id}`}
        x={lx}
        y={ly}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#1e293b"
        fontSize={segments.length > 8 ? 10 : 12}
        fontWeight={600}
      >
        {label}
      </text>
    );

    startDeg = endDeg;
  });

  return (
    <div className={className} style={{ width: size, height: size, position: "relative" }}>
      <svg
        viewBox={`-${vb} -${vb} ${vb * 2} ${vb * 2}`}
        width={size}
        height={size}
        style={{ display: "block" }}
      >
        <g
          style={{
            transform: `rotate(${rotationDeg}deg)`,
            transformOrigin: "0px 0px",
            transition: "transform 2.8s cubic-bezier(0.18, 0.85, 0.22, 1)",
          }}
        >
          {arcs}
          {labels}
          <circle cx={cx} cy={cy} r={innerR} fill="#ffffff" stroke="#94a3b8" strokeWidth={2} />
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#0f172a"
            fontSize={13}
            fontWeight={700}
          >
            SPIN
          </text>
        </g>
        <polygon points={`0,-${outerR + 4} 14,-${outerR - 18} -14,-${outerR - 18}`} fill="#0f172a" />
      </svg>
    </div>
  );
}
